import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { LuChevronDown } from "react-icons/lu";
import { css, cva, type RecipeVariantProps } from '../styled-system/css';
import { styled } from '../styled-system/jsx';
import type { SystemStyleObject } from '../styled-system/types';
import { forwardRef } from 'react';

const ChevronIcon = styled(LuChevronDown, {
    base: {
        position: 'absolute',
        right: '4',
    }
});

const accordionRecipe = cva({
    base: {
        width: '100%',
    },
    variants: {
        visual: {
            default: {
                bg: 'neutral.100',
                rounded: 'lg',
                boxShadow: 'md',
            },
            ghost: {
                bg: 'transparent',
            },
        },
    },
    defaultVariants: {
        visual: 'default',
    },
});

const triggerRecipe = cva({
    base: {
        width: '100%',
        display: 'flex',
        gap: '2',
        alignItems: 'center',
        p: '4',
        fontWeight: 'bold',
        color: 'main',
        transition: 'all 0.2s',
        fontSize: '16px',

        _hover: {
            bg: 'neutral.100',
        },

        '& > svg': {
            transition: 'transform 300ms cubic-bezier(0.87, 0, 0.13, 1)',
        },

        '&[data-state="open"] > svg': {
            transform: 'rotate(180deg)',
        },
    },
});

const contentRecipe = cva({
    base: {
        overflow: 'hidden',
        fontSize: '15px',
        color: 'main.text.2',
        bg: 'neutral.100',

        '&[data-state="open"]': {
            animation: 'slideDown 300ms cubic-bezier(0.87, 0, 0.13, 1)',
        },
        '&[data-state="closed"]': {
            animation: 'slideUp 300ms cubic-bezier(0.87, 0, 0.13, 1)',
        },
    },
});

const contentWrapperRecipe = cva({
    base: {
        overflowY: 'auto',
    },
});

const StyledAccordion = styled(AccordionPrimitive.Root, {
    base: {
        display: 'flex',
        flexDirection: 'column',
    },
});

const StyledItem = styled(AccordionPrimitive.Item, {
    base: {
        overflow: 'hidden',
        marginTop: '1px',
        flexShrink: 0,

        '&:first-child': {
            marginTop: 0,
            borderTopLeftRadius: 'lg',
            borderTopRightRadius: 'lg',
        },

        '&:last-child': {
            borderBottomLeftRadius: 'lg',
            borderBottomRightRadius: 'lg',
        },
    },
});

type AccordionItemProps = {
    trigger: React.ReactNode;
    children: React.ReactNode;
    value: string;
    css?: SystemStyleObject;
    maxHeight?: string | number;
};

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
    ({ trigger, children, value, css: cssProp, maxHeight = '400px' }, ref) => (
        <StyledItem value={value} ref={ref}>
            <AccordionPrimitive.Trigger className={triggerRecipe()}>
                {trigger}
                <ChevronIcon />
            </AccordionPrimitive.Trigger>
            <AccordionPrimitive.Content className={contentRecipe()}>
                <div className={css(contentWrapperRecipe.raw(), { maxHeight })}>
                    <div className={css({ p: '4' })}>{children}</div>
                </div>
            </AccordionPrimitive.Content>
        </StyledItem>
    )
);

AccordionItem.displayName = 'AccordionItem';

type AccordionVariants = RecipeVariantProps<typeof accordionRecipe>;

type AccordionProps = AccordionPrimitive.AccordionSingleProps &
    AccordionVariants & {
        css?: SystemStyleObject;
    };

export const Accordion = ({
    children,
    visual,
    css: cssProp,
    ...props
}: AccordionProps) => {
    const accordionProps = accordionRecipe.raw({ visual });

    return (
        <StyledAccordion {...props} className={css(accordionProps, cssProp)}>
            {children}
        </StyledAccordion>
    );
};

export { AccordionItem };